import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import clsx from "clsx";
import rg4js from "raygun4js";
import { useEffect, useMemo, useRef, useState } from "react";
import { InfoCircleFill } from "react-bootstrap-icons";
import { useParams } from "react-router-dom";
import {
  type CaseFileLite,
  type CaseFilePage,
  CaseFileType,
  DocumentStatus,
  Source,
} from "@/briefpoint-client";
import { useCaseFileApi } from "@/hooks/useApi";
import useBatchErrorToasts from "@/hooks/useBatchErrorToasts";
import { ACCEPTABLE_FILE_TYPES_WITH_IMAGES } from "@/lib/constants";
import { normalizeError, sanitizeFiles, uploadFilesToAzure } from "@/lib/utils";
import Button from "../Button";
import LeavingGuard from "../LeavingGuard";
import Loading from "../Loading";
import { toast } from "../ui/toast";
import FileList from "./FileList";
import styles from "./ProductionStep.module.scss";
import StepIndicator from "./StepIndicator";

interface Props {
  isComplete: boolean;
  setIsComplete: (isComplete: boolean) => void;
}

export default function ProductionStep(props: Props) {
  const caseFileApi = useCaseFileApi();
  const queryClient = useQueryClient();
  const batchErrorToasts = useBatchErrorToasts();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { id: caseIdParam } = useParams<{ id: string }>();

  const [estimatedProgress, setEstimatedProgress] = useState(0);
  const [hasUploadedFiles, setHasUploadedFiles] = useState(false);
  // Track the number of files uploaded so we can calculate the progress
  const [fileCount, setFileCount] = useState(0);
  const [batchId, setBatchId] = useState<string | null>(null);

  const caseFilesQueryOptions = queryOptions({
    queryKey: [
      "caseFileApi.caseFileGetCaseFiles",
      {
        caseId: caseIdParam,
        fileType: CaseFileType.Produceable,
        pageSize: 100,
      },
    ],
    queryFn: () =>
      caseFileApi.caseFileGetCaseFiles({
        caseId: caseIdParam,
        fileType: CaseFileType.Produceable,
        pageSize: 100,
      }),
    select: (data) => {
      return {
        files: data.items?.sort((a, b) => a.name.localeCompare(b.name)),
        pageCount: data.totalPages,
      };
    },
  });

  const caseFilesQuery = useQuery(caseFilesQueryOptions);

  // Upload files to Azure to create the upload batch
  const uploadFileMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const { data } = await uploadFilesToAzure({
        files,
        caseId: caseIdParam,
        type: CaseFileType.Produceable,
        caseFileApi,
        // Only let this get to 50%, since we have to wait for the batch process to get to 100%
        setEstimatedProgress: (progress) =>
          setEstimatedProgress(progress * 0.5),
      });
      return data;
    },
    onMutate: (files) => {
      setFileCount(files.length);
      // Set to incomplete state and show the progress bar
      props.setIsComplete(false);
    },
    onSuccess: async () => {
      // Trigger the polling query to check on the status of the azure upload.
      setHasUploadedFiles(true);
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to upload production documents.");
    },
  });

  const uploadBatchesQueryKey = useMemo(
    () => [
      "caseFileApi.caseFileGetCaseFileUploadBatches",
      "ProductionStep",
      { caseId: caseIdParam },
    ],
    [caseIdParam],
  );

  // When the azure upload is complete, poll the upload batch until it's empty
  const uploadBatchesQuery = useQuery({
    queryKey: uploadBatchesQueryKey,
    queryFn: () =>
      hasUploadedFiles
        ? caseFileApi.caseFileGetCaseFileUploadBatches({ caseId: caseIdParam })
        : // Hack to stop multiple requests when the component is re-mounted.
          Promise.resolve([]),
    enabled: hasUploadedFiles,
    retry: 1,
    retryDelay: 500,
    refetchInterval: (query) => {
      // When there are no documents in the batch, they have been uploaded. Stop polling.
      if (!query || query.state.data?.length === 0) {
        return false;
      }
      return 1500; // Poll every 1.5 seconds
    },
    refetchIntervalInBackground: true,
    staleTime: 0, // Always consider data stale to ensure fresh polling,
  });

  // When all the files are uploaded and the batch is processed (i.e. empty)...
  useEffect(() => {
    if (!hasUploadedFiles || !uploadBatchesQuery.data) return;

    // If the batch is not empty, calculate the progress based on remaining files
    if ((uploadBatchesQuery.data?.length || 0) > 0) {
      if (fileCount === 0) return;

      // Save the batch id to so we can get the final batch to report errors
      setBatchId(uploadBatchesQuery.data[0].id || null);

      // Track upload progress
      const remainingFiles = uploadBatchesQuery.data[0]?.files?.length || 0;
      const processedFiles = fileCount - remainingFiles;
      // Progress starts at 50% from upload, then goes to 100% as batch processes
      const batchProgress = (processedFiles / fileCount) * 50; // 50% for batch processing
      const totalProgress = 50 + batchProgress; // 50% from upload + batch progress
      setEstimatedProgress(Math.min(totalProgress, 100));
    } else {
      // If the batch is empty...

      // Reset fileCount
      setFileCount(0);

      // Clear the uploadBatchesQuery cache so this conditional works the next time we upload.
      queryClient.removeQueries({
        queryKey: uploadBatchesQueryKey,
      });

      // Optimistically update the case files query with the new files
      // Generate temporary IDs for optimistic updates
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: [
              ...(oldData.items || []),
              ...(uploadFileMutation.data?.files || []).map((file, index) => ({
                id: `temp-${uploadFileMutation.data?.id}-${index}`, // Temporary ID
                userId: uploadFileMutation.data?.userId ?? "",
                name: file.name ?? "",
                source: Source.Uploaded,
                status: DocumentStatus.Uploaded,
                uploadedByClient: true,
                isWithheld: false,
                isPrivileged: false,
                type: CaseFileType.Produceable,
              })),
            ]
              // Dedupe files with the same name. The backend will do this for us, but we need to do it here for optimistic updates.
              .filter(
                (file, index, self) =>
                  index === self.findIndex((t) => t.name === file.name),
              )
              .sort((a, b) => a.name.localeCompare(b.name)),
          };
        },
      );

      // Stop polling the batch and reveal the new files.
      setHasUploadedFiles(false);

      // Set the production step to complete
      props.setIsComplete(true);

      // Invalidate and refetch the production files query to replace optimistic data
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
    }
  }, [
    uploadBatchesQuery.data,
    hasUploadedFiles,
    uploadBatchesQueryKey,
    queryClient,
    props.setIsComplete,
    caseFilesQueryOptions.queryKey,
    uploadFileMutation.data?.id,
    uploadFileMutation.data?.files,
    uploadFileMutation.data?.userId,
    fileCount,
  ]);

  // After the batch is empty and polling is stopped, get the final batch to report errors
  const getBatchByIdQuery = useQuery({
    queryKey: ["caseFileApi.caseFileGetCaseFileUploadBatch", { batchId }],
    queryFn: () => {
      if (!caseIdParam) throw new Error("Case ID is required");
      if (!batchId) throw new Error("Batch ID is required");
      return caseFileApi.caseFileGetCaseFileUploadBatch({
        caseId: caseIdParam,
        uploadBatchId: batchId,
      });
    },
    enabled: props.isComplete && !!batchId,
  });

  // Report errors from the final batch
  useEffect(() => {
    if (!getBatchByIdQuery.data?.errorResults || !batchId || !props.isComplete)
      return;

    batchErrorToasts(getBatchByIdQuery.data.errorResults);

    // Clear the batch id from state so we're ready for the next upload
    setBatchId(null);
  }, [getBatchByIdQuery.data, batchId, props.isComplete, batchErrorToasts]);

  const deleteFileMutation = useMutation({
    mutationFn: async (file: CaseFileLite) => {
      return caseFileApi.caseFileDelete({
        caseId: caseIdParam,
        fileId: file.id,
      });
    },
    onMutate: async (file) => {
      // Cancel any outgoing refetch so that we don't overwrite any other changes
      await queryClient.cancelQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });

      // Snapshot the previous value of the query data
      const previousData = queryClient.getQueryData(
        caseFilesQueryOptions.queryKey,
      );

      // Optimistically update case files query to remove the file
      queryClient.setQueryData(
        caseFilesQueryOptions.queryKey,
        (oldData: CaseFilePage | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            items: oldData.items?.filter((item) => item.id !== file.id),
          };
        },
      );

      return { previousData };
    },
    onSuccess: (_, file) => {
      toast.success(`${file.name} was removed`);
    },
    onError: (error, __, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          caseFilesQueryOptions.queryKey,
          context.previousData,
        );
      }
      rg4js("send", {
        error: normalizeError(error),
      });
      toast.error("Unable to delete complaint file.", {
        description: "Error:",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: caseFilesQueryOptions.queryKey,
      });
    },
  });

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    e.preventDefault();
    const files = e.target.files;
    const sanitizedFiles = sanitizeFiles(files ? Array.from(files) : [], true);

    // Clear the input immediately after processing
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    // check if any file is pdf or zip
    if (sanitizedFiles?.length) {
      uploadFileMutation.mutate(sanitizedFiles);
    } else {
      toast.error("Please upload a PDF or ZIP file");
    }
  }

  const uploadProductionText =
    uploadFileMutation.isPending || hasUploadedFiles
      ? "Uploading production documents..."
      : "Upload production documents";

  const isUploading = uploadFileMutation.isPending || hasUploadedFiles;

  useEffect(() => {
    if (!isUploading) {
      props.setIsComplete(!!caseFilesQuery.data?.files?.length);
    }
  }, [isUploading, props.setIsComplete, caseFilesQuery.data?.files?.length]);

  return (
    <>
      <LeavingGuard
        when={isUploading}
        message="Your upload will stop if you leave this page."
        title="Upload in progress!"
      />
      <div className="d-flex">
        <StepIndicator
          stepNumber={2}
          isSuccess={props.isComplete}
          isLoading={isUploading}
        />
        <div className={styles.container}>
          <div className={clsx(styles.box, props.isComplete && styles.success)}>
            <Loading
              isLoading={caseFilesQuery.isLoading}
              align="left"
              className={styles.loading}
            >
              <div
                className={`${styles.contents} d-flex justify-content-between align-items-center`}
              >
                <div
                  data-testid="production-step-title"
                  className={styles.title}
                >
                  {props.isComplete ? (
                    <div
                      data-testid="production-step-title-text"
                      className="d-flex align-items-center"
                    >
                      {/* number of files will append a plus sign if there are more than 100 */}
                      {caseFilesQuery.data?.files?.length &&
                      caseFilesQuery.data.files.length > 0
                        ? `${caseFilesQuery.data.files.length}${
                            (caseFilesQuery.data.pageCount || 0) > 1 ? "+" : ""
                          } documents uploaded`
                        : "documents uploaded"}
                      <Button
                        data-testid="upload-more-button"
                        size="sm"
                        variant="link"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        Upload More
                      </Button>
                    </div>
                  ) : (
                    uploadProductionText
                  )}
                </div>
                <form>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="d-none"
                    onChange={handleFileChange}
                    accept={ACCEPTABLE_FILE_TYPES_WITH_IMAGES.join(",")}
                    multiple
                  />
                  {!isUploading && !props.isComplete && (
                    <Button
                      data-testid="upload-production-button"
                      size="sm"
                      className={styles.uploadButton}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload Production
                    </Button>
                  )}
                </form>
              </div>
            </Loading>
            {isUploading && (
              <div
                className={styles.loaderBar}
                style={{ width: `${estimatedProgress}%` }}
              />
            )}
            <div className={styles.infoBox}>
              <div className={styles.infoContent}>
                <div className={styles.infoTitle}>
                  Accepted: PDF, GIF, JPG, JPEG, PNG, or ZIP.
                </div>
                <div className={styles.infoText}>
                  Unsupported file types won't be uploaded.
                </div>
              </div>
            </div>
            {props.isComplete &&
              caseFilesQuery.data?.files &&
              caseFilesQuery.data.files.length > 0 && (
                <FileList
                  files={caseFilesQuery.data.files}
                  onDelete={deleteFileMutation.mutate}
                />
              )}
          </div>
        </div>
      </div>
    </>
  );
}
